// src/attribution/model.ts
var ATTRIBUTION_KINDS = [
  "tag",
  "label",
  "group",
  "entity"
];
var WRITABLE_SUBJECT_KINDS = [
  "address",
  "transaction",
  "entity",
  "event",
  "internal_tx"
];
function isWritableSubject(subject) {
  return WRITABLE_SUBJECT_KINDS.includes(subject.kind);
}
var KIND_LABEL = {
  tag: "tag",
  label: "label",
  group: "group",
  entity: "entity"
};
var ASSIGNMENT_ATTRIBUTE_KEYS = Object.keys({
  visibility: 0,
  confidence: 0,
  reason: 0,
  evidenceUrl: 0,
  validFrom: 0,
  validTo: 0
});
function subjectKey(subject) {
  switch (subject.kind) {
    case "address":
      return `address:${subject.chainId}:${subject.address.toLowerCase()}`;
    case "transaction":
      return `transaction:${subject.canonicalTxId}`;
    case "entity":
      return `entity:${subject.entityId}`;
    case "event":
      return `event:${subject.chainId ?? "_"}:${subject.canonicalTxId}:${subject.logIndex}`;
    case "internal_tx":
      return `internal_tx:${subject.canonicalTxId}:${subject.internalIndex}`;
  }
}
function effectiveWriteScope(preferred, options) {
  return options.shared && preferred === "private" ? "tenant" : preferred;
}
function subjectTargetId(subject) {
  switch (subject.kind) {
    case "address":
      return subject.address;
    case "transaction":
      return subject.canonicalTxId;
    case "entity":
      return subject.entityId;
    case "event":
      return `${subject.canonicalTxId}#evt#${subject.logIndex}`;
    case "internal_tx":
      return `${subject.canonicalTxId}#int#${subject.internalIndex}`;
  }
}
function subjectChainId(subject) {
  switch (subject.kind) {
    case "address":
      return subject.chainId;
    case "event":
      return subject.chainId;
    // A transaction-scope assignment is chain-agnostic in commons, and an
    // entity spans chains by definition. Sending a chain for either writes a
    // key the read side does not rebuild.
    default:
      return void 0;
  }
}

// src/attribution/capability.ts
var BELIEF = [
  "confidence",
  "reason",
  "evidenceUrl"
];
var WINDOW = ["validFrom", "validTo"];
var EMPTY = [];
var TABLE = {
  address: {
    default: ["visibility", ...BELIEF, ...WINDOW],
    // A tag files an address under a heading; it does not assert a fact with a
    // lifetime, so no window.
    tag: ["visibility", ...BELIEF]
  },
  transaction: {
    // No visibility: the tx patch route has no ABAC carriers.
    default: BELIEF,
    tag: BELIEF
  },
  entity: {
    default: ["visibility", ...BELIEF, ...WINDOW],
    tag: ["visibility", ...BELIEF]
  },
  event: {
    default: BELIEF
  },
  internal_tx: {
    default: BELIEF
  }
};
function attributionCapability(subject, kind) {
  const row = TABLE[subject.kind];
  return { accepts: row[kind] ?? row.default, requires: EMPTY };
}
function capabilityAccepts(subject, kind, attribute) {
  return attributionCapability(subject, kind).accepts.includes(attribute);
}
function pruneToCapability(subject, kind, attributes) {
  const { accepts } = attributionCapability(subject, kind);
  const out = {};
  for (const key of Object.keys(attributes)) {
    if (accepts.includes(key)) {
      Object.assign(out, { [key]: attributes[key] });
    }
  }
  return out;
}
function missingRequired(subject, kind, attributes) {
  return attributionCapability(subject, kind).requires.filter((key) => {
    const value = attributes[key];
    return value === void 0 || value === null || value === "";
  });
}

// src/attribution/eligibility.ts
var ADDRESS_BOUND_SUBJECTS = [
  "address",
  "entity"
];
var addressBoundStaysOnAddresses = (subject, _kind, item) => !ADDRESS_BOUND_SUBJECTS.includes(subject.kind) && item.address ? "Bound to an address \u2014 attach it to the address instead" : null;
var RULES = [addressBoundStaysOnAddresses];
function attributionRejection(subject, kind, item) {
  for (const rule of RULES) {
    const reason = rule(subject, kind, item);
    if (reason) return reason;
  }
  return null;
}

// src/attribution/candidates.ts
var CANDIDATE_LIMIT = 20;
function toAttributionCandidate(row) {
  const r = row ?? {};
  const id = String(r.id ?? "");
  const text = (key) => {
    const value = r[key];
    return value == null || value === "" ? void 0 : String(value);
  };
  return {
    id,
    name: text("name") ?? id,
    color: text("color"),
    address: text("address")
  };
}
function attributionCandidateLoader(list, subject) {
  return async (kind, search) => {
    const rows = await list(kind, { search, limit: CANDIDATE_LIMIT });
    return rows.map((row) => {
      const candidate = toAttributionCandidate(row);
      const ineligible = subject ? attributionRejection(subject, kind, candidate) : null;
      return {
        id: candidate.id,
        name: candidate.name ?? candidate.id,
        ...candidate.color ? { color: candidate.color } : {},
        ...candidate.address ? { address: candidate.address } : {},
        ...ineligible ? { ineligible } : {}
      };
    });
  };
}

// src/attribution/wire.ts
function defined(source) {
  const out = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== void 0 && value !== "") out[key] = value;
  }
  return out;
}
function clearedAttributes(previous, next) {
  if (!previous) return [];
  const empty = (value) => value === void 0 || value === null || value === "";
  return Object.keys(previous).filter(
    (key) => !empty(previous[key]) && empty(next[key])
  );
}
function effectiveVisibilityScope(visibility) {
  const named = visibility.teamIds?.length ?? 0;
  return visibility.scope === "team" && named === 0 ? "private" : visibility.scope;
}
function restVisibility(visibility) {
  if (!visibility) return {};
  switch (effectiveVisibilityScope(visibility)) {
    case "private":
      return { user_scoped: true };
    case "team":
      return {
        user_scoped: false,
        visible_to_team_ids: [...visibility.teamIds ?? []]
      };
    // An overlay's key has ONE sharing bit — `user_scoped` — so this path
    // cannot tell a tenant-wide row from an org- or customer-wide one. All
    // three write the same ownerless row.
    //
    // No resource offers `org` / `customer` through this path today
    // (`PHASE_1_WRITE_SCOPES` stops at `tenant`), so the collapse is currently
    // unreachable. It is a loaded gun all the same: the policy invites widening
    // the rungs "without touching a component", which is true of the READ axis
    // and false here — widening the WRITE rungs for an overlay resource would
    // silently make two different choices write identical rows.
    // `writePathCollapses` states that, and a test refuses the combination.
    case "tenant":
    case "org":
    case "customer":
      return { user_scoped: false };
  }
}
function recordVisibilityReport(visibility, fallback = "private") {
  const requested = visibility?.scope ?? fallback;
  const effective = effectiveVisibilityScope(visibility ?? { scope: fallback });
  const { scope, visibleToTeamIds } = recordVisibility(visibility, fallback);
  return {
    scope,
    visibleToTeamIds,
    requested,
    effective,
    ...effective !== requested ? { downgraded: { from: requested, to: effective, reason: "no-teams" } } : {}
  };
}
function recordVisibility(visibility, fallback = "private") {
  const teamIds = [...visibility?.teamIds ?? []];
  switch (effectiveVisibilityScope(visibility ?? { scope: fallback })) {
    case "private":
      return { scope: "user", visibleToTeamIds: [] };
    case "team":
      return { scope: "user", visibleToTeamIds: teamIds };
    case "tenant":
      return { scope: "tenant", visibleToTeamIds: [] };
    case "org":
      return { scope: "org", visibleToTeamIds: [] };
    case "customer":
      return { scope: "customer", visibleToTeamIds: [] };
  }
}
var WIRE_ATTRIBUTE_NAME = {
  confidence: "confidence",
  reason: "reason",
  evidenceUrl: "evidence_url",
  validFrom: "valid_from",
  validTo: "valid_to"
};
function clearedWireNames(cleared) {
  return cleared.map((key) => WIRE_ATTRIBUTE_NAME[key]).filter((name) => Boolean(name));
}
function toRestBody(attributes, cleared = []) {
  const fields_cleared = clearedWireNames(cleared);
  return {
    ...defined({
      confidence: attributes.confidence,
      reason: attributes.reason,
      evidence_url: attributes.evidenceUrl,
      valid_from: attributes.validFrom,
      valid_to: attributes.validTo
    }),
    ...fields_cleared.length > 0 ? { fields_cleared } : {},
    ...restVisibility(attributes.visibility)
  };
}
function toGraphqlInput(attributes) {
  const rest = restVisibility(attributes.visibility);
  return defined({
    userScoped: rest.user_scoped,
    visibleToUserIds: rest.visible_to_user_ids,
    visibleToTeamIds: rest.visible_to_team_ids,
    confidence: attributes.confidence,
    reason: attributes.reason,
    evidenceUrl: attributes.evidenceUrl,
    validFrom: attributes.validFrom,
    validTo: attributes.validTo
  });
}
function toTxAttach(catalogId, attributes = {}, cleared = []) {
  const fields_cleared = clearedWireNames(cleared);
  const provenance = defined({
    confidence: attributes.confidence,
    reason: attributes.reason,
    evidence_url: attributes.evidenceUrl,
    valid_from: attributes.validFrom,
    valid_to: attributes.validTo
  });
  if (fields_cleared.length > 0) provenance.fields_cleared = fields_cleared;
  return Object.keys(provenance).length === 0 ? catalogId : { catalog_id: catalogId, ...provenance };
}
var TX_PATCH_FIELDS = {
  tag: { add: "tags_add", remove: "tags_remove" },
  label: { add: "labels_add", remove: "labels_remove" },
  group: { add: "groups_add", remove: "groups_remove" },
  entity: { add: "entities_add", remove: "entities_remove" }
};
function txPatchBody(kind, catalogId, op, attributes = {}, cleared = []) {
  return {
    [TX_PATCH_FIELDS[kind][op]]: [
      op === "add" ? toTxAttach(catalogId, attributes, cleared) : catalogId
    ]
  };
}

// src/plans/index.ts
var UsageEventKind = {
  PER_TX: "per_tx"};
var EnforcementMode = { SOFT: "soft", HARD: "hard" };
var OveragePolicy = { ALLOW: "allow", THROTTLE: "throttle", REJECT: "reject" };
var ent = (name, enabled = true) => ({ name, enabled });
var win = (granularity) => ({ granularity });
var PLAN_FREE = {
  id: "free",
  display_name: "Free",
  description: "Trial / community tier with conservative monthly cap.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring", false),
    ent("price_enrichment", false),
    ent("triggers", false),
    ent("webhook_callbacks", false),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("actors"),
    ent("situations")
  ],
  quotas: [
    { id: "free_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e4, soft_limit: 9e3, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT },
    { id: "free_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 60, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT }
  ]
};
var PLAN_PRO = {
  id: "pro",
  display_name: "Pro",
  description: "Paid tier with higher caps and full enrichment surface.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("actors"),
    ent("situations")
  ],
  quotas: [
    { id: "pro_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e6, soft_limit: 9e5, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE },
    { id: "pro_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 600, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE }
  ]
};
var PLAN_ENTERPRISE = {
  id: "enterprise",
  display_name: "Enterprise",
  description: "Custom-quota tier with soft enforcement (audit only).",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("actors"),
    ent("situations"),
    ent("sla_99_99")
  ],
  quotas: [
    { id: "enterprise_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 0, enforcement: EnforcementMode.SOFT, overage: OveragePolicy.ALLOW }
  ]
};
function bundledPlans() {
  return [PLAN_FREE, PLAN_PRO, PLAN_ENTERPRISE];
}
var ENTITLEMENT = {
  /** Actor rankings and conduct, including their tenancy defaults. */
  ACTORS: "actors",
  SITUATIONS: "situations",
  TRIGGERS: "triggers"};
new Set(
  bundledPlans().flatMap((plan) => plan.entitlements.map((e) => e.name))
);

// src/tenancy/record-scopes.ts
var TX_SCOPES = [
  {
    id: "user",
    label: "My transactions",
    description: "Transactions saved to your own private workspace.",
    kind: "persisted"
  },
  {
    id: "tenant",
    label: "Tenant",
    description: "Everything shared across your current tenant.",
    kind: "persisted"
  },
  {
    id: "org",
    label: "Organization",
    description: "The organization-level feed, shared across this org's tenants.",
    kind: "persisted"
  },
  {
    id: "customer",
    label: "Customer",
    description: "The customer-level feed, shared across the whole account.",
    kind: "persisted"
  },
  {
    id: "global",
    label: "Global",
    description: "The shared global record of on-chain transactions, system-wide.",
    kind: "persisted"
  },
  {
    id: "latest",
    label: "Latest blocks",
    description: "Live transactions from the latest block on each preview chain.",
    kind: "live"
  }
];
var TX_TENANCY_SCOPES = TX_SCOPES.filter(
  (s) => s.kind === "persisted"
);

// src/attribution/scope.ts
var RECORD_RESOURCES = [
  "watch",
  "tracer",
  "alertRule",
  "groupingRule",
  "pipeline",
  "savedView"
];
function isRecordResource(resource) {
  return RECORD_RESOURCES.includes(resource);
}
var WRITE_PATH = {
  transaction: "none",
  address: "overlay",
  entity: "overlay",
  label: "overlay",
  tag: "overlay",
  group: "overlay",
  protocol: "registry",
  actor: "none",
  situation: "none",
  watch: "record",
  tracer: "record",
  alertRule: "record",
  groupingRule: "record",
  pipeline: "record",
  savedView: "record"
};
var PHASE_1_RESOLVE_SCOPES = ["USER", "TENANT", "GLOBAL"];
var PHASE_1_WRITE_SCOPES = ["private", "team", "tenant"];
var PHASE_1_DEFAULT_WRITE_SCOPE = "tenant";
var PHASE_1_DEFAULT_RESOLVE_SCOPE = "USER";
function resourcePolicy(overrides = {}) {
  return {
    enabled: true,
    resolvedView: true,
    showProvenance: true,
    showLogos: true,
    resolveScopes: PHASE_1_RESOLVE_SCOPES,
    writeScopes: PHASE_1_WRITE_SCOPES,
    defaultWriteScope: PHASE_1_DEFAULT_WRITE_SCOPE,
    defaultResolveScope: PHASE_1_DEFAULT_RESOLVE_SCOPE,
    configurableWriteDefault: true,
    configurableReadDefault: true,
    ...overrides
  };
}
var DISABLED_RESOURCE_POLICY = {
  enabled: false,
  resolvedView: false,
  showProvenance: false,
  showLogos: false,
  resolveScopes: [],
  writeScopes: ["private"],
  defaultWriteScope: "private",
  defaultResolveScope: "USER",
  configurableWriteDefault: false,
  configurableReadDefault: false
};
function attributionPolicies(overrides = {}) {
  const base = {
    // Transactions already receive a fully-resolved payload (with alternatives)
    // from `transactions-mgmt-api`, so the widen ladder is safe to show in full.
    // The WRITE axis still takes no saved default: the patch route carries no
    // ABAC fields, so a transaction overlay is tenant-wide by construction and
    // there is nothing to choose. The READ axis does, now that the batch resolve
    // takes a tier — while it could not, a saved default would have applied to a
    // transaction's detail page and not to the same transaction's row in a table.
    transaction: resourcePolicy({
      configurableWriteDefault: false,
      configurableReadDefault: true
    }),
    address: resourcePolicy(),
    entity: resourcePolicy(),
    label: resourcePolicy(),
    tag: resourcePolicy(),
    group: resourcePolicy(),
    // Warehouse reads use the same saved-default ladder. Actors have no write
    // surface; their totals and counts resolve one transaction copy per tier.
    actor: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      configurableWriteDefault: false,
      readCapability: ENTITLEMENT.ACTORS,
      readHint: "Tier used for actor rankings, launch counts and findings",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    watch: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      defaultResolveScope: "USER",
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"],
      writeCapability: ENTITLEMENT.TRIGGERS,
      readHint: "Watches visible at this tier; does not change monitoring or recipients",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    situation: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      defaultResolveScope: "TENANT",
      configurableWriteDefault: false,
      readCapability: ENTITLEMENT.SITUATIONS,
      readHint: "Tier used for detected situations and their details",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    // Protocol definitions are shared decoding machinery, so the two personal
    // rungs are deliberately absent: `private` / `team` would mean "decode this
    // protocol differently for just me", which makes one tenant's transactions
    // irreproducible for their colleagues. Tenant-wide is the narrowest rung
    // that keeps a decode explainable to everyone who reads it, and it stays the
    // default.
    //
    // The two rungs ABOVE it are offered, and were missing rather than excluded:
    // the reasoning above rules out going narrower than a tenant and says
    // nothing about going wider, while the backend has always accepted
    // `customer` / `org` / `tenant` / `user` and the store folds all four. A
    // customer running several orgs on one decoder had to author the same patch
    // once per tenant and keep them in step by hand — which is the drift the
    // ladder exists to remove.
    //
    // `system` is still absent, and that is a real exclusion: the system rung is
    // a published definition rather than an overlay, and the API refuses a patch
    // there (`POST /protocols/publish` is its own internal-only door).
    protocol: resourcePolicy({
      writeScopes: ["tenant", "org", "customer"],
      defaultWriteScope: "tenant",
      configurableWriteDefault: false,
      configurableReadDefault: false
    }),
    // -- The authored records (see RECORD_RESOURCES) ----------------------
    //
    // Every default below is what the surface ALREADY did before it was asked
    // to consult this table, and deliberately so: extending the setting must
    // give people a choice, not silently move anyone's rows. That is why these
    // five do not share one default — a grouping rule has always been
    // tenant-wide and a tracer has always been owner-only, and unifying them
    // here would have re-homed live rows on deploy.
    //
    // None carries a read axis: there is no precedence stack to resolve, so
    // `resolvedView` is off and the read card never lists them.
    // Rung on the row + team carriers, so the whole ladder is expressible.
    tracer: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"]
    }),
    // The odd one out, and the reason `writeScopes` is per-resource: an alert
    // rule has NO tenancy rung on the wire (`ruleToWire` sends `condition`,
    // `destination_ids` and `visible_to_team_ids`, and nothing else about who
    // may see it). Owner-only and team-shared are the only two states it can
    // hold, so those are the only two offered — a `tenant` option here would
    // collect a choice the route discards, which is worse than no option.
    alertRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team"]
    }),
    // No `team`, and that is a wire fact rather than a preference: `toWire`
    // sends `scope` and nothing else about audience, so a team choice would be
    // dropped in the adapter. A `team` rung was offered on this resource once
    // and `_applies_to` narrowed only `user` rows — a "team" rule was in force
    // tenant-wide while ranking above the tenant rule it shadowed. It is not
    // coming back through a default.
    groupingRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "tenant",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // Same shape: `save` posts a `scope` and no carrier.
    pipeline: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // `org` / `customer` are real rungs on the backend enum and `_guard_scope`
    // admits them with the write verb, but the save dialog has only ever
    // offered Personal and Shared. Kept to what the product offers rather than
    // to what the wire accepts: widening is one line here the day the dialog
    // grows the options, and offering a default nobody can pick at attach time
    // is the asymmetry this table exists to avoid.
    // Personal and Shared, the two the save dialog offers. No `team` even
    // though `ViewScope` has the rung: `viewToWire` sends no `visible_to_team_ids`,
    // and the backend's tier scopes (which carry their audience in the tenancy
    // tuple) exclude TEAM — so a team-scoped view would depend on carriers this
    // client never writes. `org` / `customer` are real and admitted with the
    // write verb; they wait for the dialog to offer them.
    savedView: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant"]
    })
  };
  const out = { ...base };
  for (const [resource, override] of Object.entries(overrides)) {
    const key = resource;
    if (override) out[key] = { ...out[key] ?? DISABLED_RESOURCE_POLICY, ...override };
  }
  return out;
}
var ATTRIBUTION_POLICIES = attributionPolicies();
function policyFor(resource, policies = ATTRIBUTION_POLICIES) {
  return policies[resource] ?? DISABLED_RESOURCE_POLICY;
}
function resolveWriteScope(overrides, resource, policies = ATTRIBUTION_POLICIES) {
  const policy = policyFor(resource, policies);
  const override = overrides?.[resource];
  if (override && policy.writeScopes.includes(override)) return override;
  return policy.defaultWriteScope;
}
function resolveReadScope(overrides, resource, policies = ATTRIBUTION_POLICIES) {
  const policy = policyFor(resource, policies);
  const override = overrides?.[resource];
  if (override && policy.resolveScopes.includes(override)) return override;
  return policy.defaultResolveScope;
}
function writeScopeResources(policies = ATTRIBUTION_POLICIES) {
  return Object.keys(policies).filter(
    (resource) => policies[resource].enabled && policies[resource].configurableWriteDefault
  );
}
function readScopeResources(policies = ATTRIBUTION_POLICIES) {
  return Object.keys(policies).filter(
    (resource) => policies[resource].enabled && policies[resource].resolvedView && policies[resource].configurableReadDefault
  );
}
var RECORD_TIER_TO_READ_SCOPE = {
  user: "USER",
  tenant: "TENANT",
  org: "ORG",
  customer: "CUSTOMER",
  global: "GLOBAL"
};
var WRITE_SCOPE_TO_ANALYTICS = {
  private: "user",
  team: "user",
  tenant: "tenant",
  org: "org",
  customer: "customer"
};
var READ_SCOPE_TO_ANALYTICS = {
  USER: "merged",
  TENANT: "tenant",
  ORG: "org",
  CUSTOMER: "customer",
  GLOBAL: "system"
};
function attributionLadderGap(policies = ATTRIBUTION_POLICIES) {
  const offered = new Set(
    readScopeResources(policies).filter((resource) => policies[resource].readKind !== "data").flatMap((resource) => policies[resource].resolveScopes)
  );
  return TX_TENANCY_SCOPES.map((tier) => tier.id).filter(
    (tier) => !offered.has(RECORD_TIER_TO_READ_SCOPE[tier])
  );
}
var WRITE_SCOPE_LABEL = {
  private: "Private to me",
  team: "Specific teams",
  tenant: "Whole tenant",
  org: "Whole organization",
  customer: "Whole customer"
};
var WRITE_SCOPE_MEANING = {
  private: "Only you can see it \u2014 teammates opening this address will not.",
  team: "Your teams can see it. Everyone else in the tenant will not.",
  tenant: "Everyone in your tenant can see it.",
  org: "Shared across the organization.",
  customer: "Shared across the whole account."
};
var RESOLVE_SCOPE_LABEL = {
  USER: "Your view",
  TENANT: "Tenant",
  ORG: "Organization",
  CUSTOMER: "Customer",
  GLOBAL: "Global"
};
function tierWeightToScope(tierWeight) {
  if (tierWeight >= 60) return "user";
  if (tierWeight >= 40) return "tenant";
  if (tierWeight > 30) return "org";
  if (tierWeight >= 30) return "default";
  if (tierWeight >= 20) return "entity";
  return "provider";
}
var SCOPE_PROVENANCE_LABEL = {
  user: "Your overlay",
  tenant: "Tenant overlay",
  org: "Organization overlay",
  default: "System default",
  entity: "Inherited from entity",
  provider: "Provider candidate"
};
function provenanceLabel(tierWeight) {
  return SCOPE_PROVENANCE_LABEL[tierWeightToScope(tierWeight)];
}
var SCOPE_HINT_FLAG = "ui.scopeHints";

// src/attribution/hintState.ts
function writeHintState(subject, kinds, defaultVisibility, options = {}) {
  const configurable = kinds.filter((kind) => capabilityAccepts(subject, kind, "visibility"));
  if (configurable.length === 0) {
    return { kind: "fixed", perKind: [], teamIds: [], raised: false };
  }
  const resolved = configurable.map((kind) => ({ kind, visibility: defaultVisibility(kind) }));
  const perKind = resolved.map(({ kind, visibility }) => ({ kind, scope: visibility.scope }));
  const scopes = [...new Set(perKind.map((e) => e.scope))];
  const raised = Boolean(
    options.shared && options.configuredScope && resolved.some(
      ({ kind, visibility }) => visibility.scope === "tenant" && options.configuredScope?.(kind) === "private"
    )
  );
  return {
    kind: scopes.length === 1 ? "uniform" : "mixed",
    ...scopes.length === 1 ? { scope: scopes[0] } : {},
    perKind,
    teamIds: resolved.find((e) => e.visibility.scope === "team")?.visibility.teamIds ?? [],
    raised
  };
}
function readHintState(policy, scope) {
  return {
    visible: policy.enabled && policy.resolvedView,
    scope,
    ownView: scope === "USER"
  };
}
function surfaceHintState(records, subjects) {
  const tiers = new Set(subjects.map((each) => each.scope));
  const agreed = tiers.size === 1 ? subjects[0] : void 0;
  return {
    mismatched: Boolean(records.writesLandIn) && records.writesLandIn !== records.label,
    attributionNotable: !agreed || !agreed.ownView && RESOLVE_SCOPE_LABEL[agreed.scope] !== records.label,
    ...agreed ? { scope: agreed.scope } : {},
    ownView: Boolean(agreed?.ownView)
  };
}

// src/attribution/plan.ts
var IneligibleAttributionError = class extends Error {
  constructor(reason) {
    super(reason);
    this.name = "IneligibleAttributionError";
  }
};
var UnsupportedSubjectError = class extends Error {
  constructor(subjectKind) {
    super(`Attribution isn't supported on ${SUBJECT_NOUN[subjectKind] ?? "this"} yet.`);
    this.subjectKind = subjectKind;
    this.name = "UnsupportedSubjectError";
  }
  subjectKind;
};
var ATTRIBUTE_PROBE = {
  visibility: { scope: "private" },
  confidence: 0,
  reason: "",
  evidenceUrl: "",
  validFrom: 0,
  validTo: 0
};
var SUBJECT_NOUN = {
  entity: "an entity",
  event: "an event",
  internal_tx: "an internal transaction"
};
function addressRef(subject, what) {
  if (subject.kind !== "address") {
    throw new Error(`${what} applies to an address, not a ${subject.kind}`);
  }
  return { chainId: subject.chainId, address: subject.address };
}
function planAttribution(write, defaults = {}) {
  const { subject, kind, candidate, target = "overlay" } = write;
  if (!isWritableSubject(subject)) throw new UnsupportedSubjectError(subject.kind);
  const rejection = attributionRejection(subject, kind, candidate);
  if (rejection) throw new IneligibleAttributionError(rejection);
  const attributes = pruneToCapability(subject, kind, {
    ...defaults,
    ...write.attributes
  });
  const accepted = new Set(Object.keys(pruneToCapability(subject, kind, ATTRIBUTE_PROBE)));
  const cleared = (write.cleared ?? []).filter((key) => accepted.has(key));
  switch (target) {
    case "overlay":
      if (subject.kind === "transaction") {
        return {
          route: "tx-overlay",
          canonicalTxId: subject.canonicalTxId,
          kind,
          catalogId: candidate.id,
          attributes,
          cleared
        };
      }
      if (subject.kind === "address") {
        return {
          route: "address-overlay",
          ref: addressRef(subject, "a catalog overlay"),
          kind,
          catalogId: candidate.id,
          attributes,
          cleared
        };
      }
      return {
        route: "catalog-assignment",
        scope: subject.kind,
        targetId: subjectTargetId(subject),
        chainId: subjectChainId(subject),
        kind,
        catalogId: candidate.id,
        attributes,
        cleared
      };
    case "primary":
      return {
        route: "primary",
        ref: addressRef(subject, "a primary pointer"),
        entityId: candidate.id
      };
    case "membership":
      return {
        route: "membership",
        ref: addressRef(subject, "an entity membership"),
        entityId: candidate.id,
        attributes,
        // Trimmed here rather than at the form: a role of spaces is the same
        // statement as no role, and only one of the two round-trips as one.
        role: write.role?.trim() || void 0
      };
  }
}

// src/attribution/bulk.ts
var MAX_BULK_SUBJECTS = 100;
var CONCURRENCY = 4;
async function applyToMany(subjects, apply) {
  const outcome = { applied: [], failed: [] };
  const queue = [...subjects];
  const worker = async () => {
    for (; ; ) {
      const subject = queue.shift();
      if (subject === void 0) return;
      try {
        await apply(subject);
        outcome.applied.push(subject);
      } catch (reason) {
        outcome.failed.push({ subject, reason });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, subjects.length) }, worker));
  return outcome;
}
function outcomeMessage(outcome, verbPast) {
  const total = outcome.applied.length + outcome.failed.length;
  return outcome.failed.length === 0 ? `${total} ${verbPast}` : `${outcome.applied.length} of ${total} ${verbPast} \u2014 ${outcome.failed.length} failed`;
}

// src/precedence/enums.generated.ts
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};

// src/attribution/chips.ts
var SEVERITIES = Object.values(Severity);
var BUNDLE_KEY = {
  entity: "entities",
  label: "labels",
  tag: "tags",
  group: "groups"
};
var NO_ATTACHMENTS = {
  tag: [],
  label: [],
  group: [],
  entity: []
};
function toCatalogChip(item) {
  if (typeof item === "string") return item ? { id: item, name: item } : null;
  if (item && typeof item === "object") {
    const o = item;
    const id = String(o.id ?? o.catalog_id ?? "");
    if (!id) return null;
    const name = o.name == null || o.name === "" ? id : String(o.name);
    const color = o.color == null || o.color === "" ? void 0 : String(o.color);
    return { id, name, color, ...chipProvenance(o) };
  }
  return null;
}
function readGlassBoxProvenance(o) {
  const confidence = typeof o.confidence === "number" && Number.isFinite(o.confidence) ? o.confidence : void 0;
  const explanation = Array.isArray(o.explanation) ? o.explanation.filter((b) => typeof b === "string" && b !== "") : [];
  const lifecycle = o.lifecycle == null || o.lifecycle === "" ? void 0 : String(o.lifecycle);
  return {
    confidence,
    explanation: explanation.length > 0 ? explanation : void 0,
    lifecycle
  };
}
function chipProvenance(o) {
  return readGlassBoxProvenance(o);
}
function catalogChipsFrom(items) {
  if (!Array.isArray(items)) return [];
  return items.map(toCatalogChip).filter((c) => c !== null);
}
function attachedFromCatalogBlock(block) {
  const b = block ?? {};
  return {
    tag: catalogChipsFrom(b.tags),
    label: catalogChipsFrom(b.labels),
    group: catalogChipsFrom(b.groups),
    entity: catalogChipsFrom(b.entities)
  };
}
function attributionBundle(attached) {
  return {
    tags: attached.tag,
    labels: attached.label,
    groups: attached.group,
    entities: attached.entity
  };
}
function foldPrimaryEntity(attached, primaryEntityId) {
  const primary = primaryEntityId || void 0;
  if (!primary || attached.entity.some((chip) => chip.id === primary)) return { attached };
  return {
    // First, because it is the headline.
    attached: { ...attached, entity: [{ id: primary, name: primary }, ...attached.entity] },
    foldedPrimary: primary
  };
}
function attachedFromAssignments(rows, options = {}) {
  const attached = { tag: [], label: [], group: [], entity: [] };
  const seen = {
    tag: /* @__PURE__ */ new Set(),
    label: /* @__PURE__ */ new Set(),
    group: /* @__PURE__ */ new Set(),
    entity: /* @__PURE__ */ new Set()
  };
  for (const row of rows) {
    const kind = String(row.catalog_kind ?? "");
    const id = row.catalog_id == null ? "" : String(row.catalog_id);
    if (!id || !(kind in attached) || seen[kind].has(id)) continue;
    seen[kind].add(id);
    attached[kind].push({ id, name: id });
  }
  return foldPrimaryEntity(attached, options.primaryEntityId);
}
function namedAttributionBundle(attached, lookup, options = {}) {
  const { primaryEntityId, fallbackName } = options;
  const items = (kind) => attached[kind].map((chip) => {
    const row = lookup(kind, chip.id);
    const own = chip.name && chip.name !== chip.id ? chip.name : void 0;
    return {
      id: chip.id,
      name: row?.name ?? own ?? fallbackName?.(chip.id) ?? chip.id,
      ...row?.color ?? chip.color ? { color: row?.color ?? chip.color } : {},
      ...kind === "entity" && primaryEntityId && chip.id === primaryEntityId ? { isPrimary: true } : {}
    };
  });
  return {
    entities: items("entity"),
    labels: items("label"),
    tags: items("tag"),
    groups: items("group")
  };
}
function overlayAttributionBundle(overlays) {
  const primaryOf = (kind) => overlays.primary?.find((p) => p.kind === kind)?.catalogId;
  return {
    entities: (overlays.entities ?? []).map((e) => ({
      id: e.id,
      name: e.name || e.id,
      logo: e.logo,
      color: e.color,
      confidence: e.confidence,
      entityType: e.type,
      isPrimary: e.id === primaryOf("entity")
    })),
    tags: (overlays.tags ?? []).map((h) => hitChip(h, primaryOf("tag"))),
    labels: (overlays.labels ?? []).map((h) => hitChip(h, primaryOf("label"))),
    groups: (overlays.groups ?? []).map((h) => hitChip(h, primaryOf("group")))
  };
}
function severityOf(value) {
  const key = typeof value === "string" ? value.toLowerCase() : "";
  return SEVERITIES.includes(key) ? { severity: key } : {};
}
function hitChip(hit, primaryId) {
  return {
    id: hit.catalogId,
    name: hit.name || hit.catalogId,
    color: hit.color,
    logo: hit.logo,
    confidence: hit.confidence,
    lifecycle: hit.lifecycle,
    explanation: hit.explanation,
    // The weight behind the name. A label is not only a name — `Sanctioned: OFAC`
    // is an accusation — and this is the one field on the chip that is not
    // cosmetic. Absent stays absent: no severity is not `info`.
    ...severityOf(hit.severity),
    // The resolver already marks the winner; `primary` on the bundle is the
    // curated pointer. Either one makes this the headline.
    isPrimary: Boolean(hit.isPrimary) || hit.catalogId === primaryId
  };
}

// src/attribution/writer.ts
function createAttributionWriter(ports) {
  const { addresses, patchTransaction, invalidate, defaultVisibility } = ports;
  const send = async (plan) => {
    switch (plan.route) {
      case "address-overlay":
        await addresses.attachAssignment(plan.ref.chainId, plan.ref.address, {
          catalog_kind: plan.kind,
          catalog_id: plan.catalogId,
          ...toRestBody(plan.attributes, plan.cleared)
        });
        return;
      case "tx-overlay":
        await patchTransaction(
          plan.canonicalTxId,
          txPatchBody(plan.kind, plan.catalogId, "add", plan.attributes, plan.cleared)
        );
        return;
      case "catalog-assignment": {
        if (!ports.postCatalogAssignment) {
          throw new Error(
            `[attribution] this host cannot attribute a ${plan.scope}: no catalog-assignments port`
          );
        }
        await ports.postCatalogAssignment({
          scope: plan.scope,
          target_id: plan.targetId,
          ...plan.chainId ? { chain_id: plan.chainId } : {},
          catalog_kind: plan.kind,
          catalog_id: plan.catalogId,
          ...toRestBody(plan.attributes, plan.cleared)
        });
        return;
      }
      case "primary":
        await addresses.setPrimary(plan.ref.chainId, plan.ref.address, plan.entityId);
        return;
      case "membership":
        await addresses.putEntityMapping(plan.ref.chainId, plan.ref.address, {
          entity_id: plan.entityId,
          role: plan.role,
          confidence: plan.attributes.confidence,
          valid_from: plan.attributes.validFrom,
          valid_to: plan.attributes.validTo
        });
    }
  };
  return {
    async assign(write) {
      await send(planAttribution(write, { visibility: defaultVisibility(write.kind) }));
      invalidate(write.subject);
    },
    async unassign(subject, kind, catalogId, target = "overlay") {
      switch (target) {
        case "overlay":
          if (subject.kind === "transaction") {
            await patchTransaction(
              subject.canonicalTxId,
              txPatchBody(kind, catalogId, "remove", {})
            );
          } else if (subject.kind !== "address") {
            if (!ports.deleteCatalogAssignment) {
              throw new Error(
                `[attribution] this host cannot detach from a ${subject.kind}: no catalog-assignments port`
              );
            }
            await ports.deleteCatalogAssignment({
              scope: subject.kind,
              target_id: subjectTargetId(subject),
              ...subjectChainId(subject) ? { chain_id: subjectChainId(subject) } : {},
              catalog_kind: kind,
              catalog_id: catalogId
            });
          } else {
            const ref = addressRef(subject, "a catalog overlay");
            await addresses.detachAssignment(ref.chainId, ref.address, {
              catalog_kind: kind,
              catalog_id: catalogId
              // No lane named on purpose. Whether the overlay went to the
              // caller's private partition or the tenant-wide one was decided by
              // the write scope in force at attach time and is not carried on a
              // resolved read — and a delete aimed at a key that does not exist
              // SUCCEEDS. Guessing here is what made a tenant-wide chip
              // undeletable while every layer reported success.
            });
          }
          break;
        case "primary": {
          const ref = addressRef(subject, "a primary pointer");
          await addresses.clearPrimary(ref.chainId, ref.address, catalogId);
          break;
        }
        case "membership": {
          const ref = addressRef(subject, "an entity membership");
          await addresses.deleteEntityMapping(ref.chainId, ref.address);
          break;
        }
      }
      invalidate(subject);
    },
    async setMembership(subject, input) {
      const ref = addressRef(subject, "an entity membership");
      await addresses.putEntityMapping(ref.chainId, ref.address, input);
      invalidate(subject);
    }
  };
}

// src/attribution/prefs.ts
var READ_ONLY_SCOPE_PREFS = {
  writeScope: {},
  resolveScope: {},
  setWriteScope: () => {
  },
  setResolveScope: () => {
  }
};
var SCOPE_SOURCE_LABEL = {
  user: "your choice",
  workspace: "set by your workspace",
  plan: "set by your plan",
  product: "product default"
};
function inheritedDefault(layers, pick, resource, offered, productDefault) {
  for (let i = (layers?.length ?? 0) - 1; i >= 0; i -= 1) {
    const layer = layers[i];
    const candidate = pick(layer)?.[resource];
    if (candidate && offered.includes(candidate)) {
      return { value: candidate, source: layer.source };
    }
  }
  return { value: productDefault, source: "product" };
}
function effectiveScopes(prefs, policies) {
  const resolveWrite = (resource) => {
    const policy = policyFor(resource, policies);
    const below = inheritedDefault(
      prefs.managed,
      (layer) => layer.writeScope,
      resource,
      policy.writeScopes,
      policy.defaultWriteScope
    );
    const own = prefs.writeScope[resource];
    const mine = own && policy.writeScopes.includes(own) ? own : null;
    return {
      value: mine ?? below.value,
      source: mine ? "user" : below.source,
      inherited: below.value,
      inheritedSource: below.source
    };
  };
  const resolveRead = (resource) => {
    const policy = policyFor(resource, policies);
    const below = inheritedDefault(
      prefs.managed,
      (layer) => layer.resolveScope,
      resource,
      policy.resolveScopes,
      policy.defaultResolveScope
    );
    const own = prefs.resolveScope[resource];
    const mine = own && policy.resolveScopes.includes(own) ? own : null;
    return {
      value: mine ?? below.value,
      source: mine ? "user" : below.source,
      inherited: below.value,
      inheritedSource: below.source
    };
  };
  return {
    writeScope: (resource) => resolveWrite(resource).value,
    readScope: (resource) => resolveRead(resource).value,
    isWriteOverridden: (resource) => resolveWrite(resource).source === "user",
    isReadOverridden: (resource) => resolveRead(resource).source === "user",
    resolveWrite,
    resolveRead
  };
}

// src/addresses/model.ts
var ADDRESS_FIELD_DEFAULTS = {
  address_type: "unknown",
  risk_level: "safe",
  severity: "info",
  lifecycle: "candidate",
  is_primary_chain: false,
  is_human_approved: false,
  system_default: false,
  confidence: 1
};
function isDefaultedField(row, field) {
  const value = row?.[field];
  if (value === null || value === void 0) return true;
  const fallback = ADDRESS_FIELD_DEFAULTS[field];
  return value === fallback;
}

// src/attribution/scored.ts
function isDefaultedConfidence(confidence) {
  return isDefaultedField({ confidence }, "confidence");
}
function scoredConfidence(confidence) {
  if (typeof confidence !== "number" || !Number.isFinite(confidence)) return void 0;
  return isDefaultedConfidence(confidence) ? void 0 : confidence;
}
function scoredAttributionItem(item) {
  if (!isDefaultedConfidence(item.confidence)) return item;
  const { confidence: _dropped, ...rest } = item;
  return rest;
}
function scoredItems(items) {
  return (items ?? []).map(scoredAttributionItem);
}
function scoredAttributionBundle(bundle) {
  return {
    ...bundle,
    entities: scoredItems(bundle.entities),
    tags: scoredItems(bundle.tags),
    labels: scoredItems(bundle.labels),
    groups: scoredItems(bundle.groups)
  };
}
function scoredOverlayBundle(overlays) {
  return scoredAttributionBundle(overlayAttributionBundle(overlays));
}

export { ASSIGNMENT_ATTRIBUTE_KEYS, ATTRIBUTION_KINDS, ATTRIBUTION_POLICIES, BUNDLE_KEY, CANDIDATE_LIMIT, DISABLED_RESOURCE_POLICY, IneligibleAttributionError, KIND_LABEL, MAX_BULK_SUBJECTS, NO_ATTACHMENTS, READ_ONLY_SCOPE_PREFS, READ_SCOPE_TO_ANALYTICS, RECORD_RESOURCES, RECORD_TIER_TO_READ_SCOPE, RESOLVE_SCOPE_LABEL, SCOPE_HINT_FLAG, SCOPE_PROVENANCE_LABEL, SCOPE_SOURCE_LABEL, TX_PATCH_FIELDS, UnsupportedSubjectError, WRITABLE_SUBJECT_KINDS, WRITE_PATH, WRITE_SCOPE_LABEL, WRITE_SCOPE_MEANING, WRITE_SCOPE_TO_ANALYTICS, addressRef, applyToMany, attachedFromAssignments, attachedFromCatalogBlock, attributionBundle, attributionCandidateLoader, attributionCapability, attributionLadderGap, attributionPolicies, attributionRejection, capabilityAccepts, catalogChipsFrom, clearedAttributes, clearedWireNames, createAttributionWriter, effectiveScopes, effectiveWriteScope, foldPrimaryEntity, isDefaultedConfidence, isRecordResource, isWritableSubject, missingRequired, namedAttributionBundle, outcomeMessage, overlayAttributionBundle, planAttribution, policyFor, provenanceLabel, pruneToCapability, readGlassBoxProvenance, readHintState, readScopeResources, recordVisibility, recordVisibilityReport, resolveReadScope, resolveWriteScope, resourcePolicy, restVisibility, scoredAttributionBundle, scoredAttributionItem, scoredConfidence, scoredOverlayBundle, subjectChainId, subjectKey, subjectTargetId, surfaceHintState, tierWeightToScope, toAttributionCandidate, toCatalogChip, toGraphqlInput, toRestBody, toTxAttach, txPatchBody, writeHintState, writeScopeResources };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map